import React, {
  ReactNode,
  forwardRef,
  DOMAttributes,
  ForwardedRef
} from 'react'
import styled from '@emotion/styled'
import { ThemeProvider } from '@emotion/react'

interface LayoutWithSidebarProps
  extends Pick<DOMAttributes<HTMLDivElement>, 'onScroll'> {
  children?: ReactNode
  /**
   * Takes a string to output a class. Related to emotion - It lets another component style it
   */
  className?: string
  head: ReactNode
  header: ReactNode
  sidebar: ReactNode
}

interface LayoutProps extends Pick<DOMAttributes<HTMLDivElement>, 'onScroll'> {
  children?: ReactNode
  /**
   * Takes a string to output a class. Related to emotion - It lets another component style it
   */
  className?: string
}

export const SIDE_PADDING = 4

const extraWideTheme = { currentMaxWidth: 'xxl' as const }

const LayoutWithSidebar = (
  {
    children,
    className,
    onScroll,
    head,
    header,
    sidebar
  }: LayoutWithSidebarProps,
  ref?: ForwardedRef<HTMLDivElement>
) => {
  return (
    <ThemeProvider theme={extraWideTheme}>
      <StyledLayout className={className} onScroll={onScroll} ref={ref}>
        {head}
        {header}
        <MainContainer>
          <LeftDiv></LeftDiv>
          <main>
            <Sidebar>{sidebar}</Sidebar>
            <Content>{children}</Content>
          </main>
          <RightDiv></RightDiv>
        </MainContainer>
      </StyledLayout>
    </ThemeProvider>
  )
}

export default forwardRef(LayoutWithSidebar)

const MainContainer = styled.div(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: `1fr minmax(0, 1440px) 1fr`,
  [theme.mediaQueries.lg]: {
    minHeight: '100vh'
  }
}))

const LeftDiv = styled.div(({ theme }) => ({
  flex: '0 0 auto',

  [theme.mediaQueries.lg]: {
    backgroundColor: theme.colors.background.secondary
  }
}))

const RightDiv = styled.div(() => ({
  flex: '0 0 auto'
}))

export const Content = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 2,
  boxSizing: 'border-box',
  padding: theme.mixins.pxSpread([
    theme.space[0],
    theme.space[4],
    theme.space[5],
    theme.space[4]
  ]),

  [theme.mediaQueries.lg]: {
    padding: theme.mixins.pxSpread([
      theme.space[5],
      theme.space[5],
      theme.space[5],
      theme.space[5]
    ])
  }
}))

export const StyledLayout = styled.div<LayoutProps>(({ theme }) => ({
  display: 'flex',
  flex: '1 0 0',
  flexDirection: 'column',
  overflow: 'auto',
  height: '100%',
  background: theme.colors.white,
  width: '100%',
  /*
   * In older browsers, specifically older versions of Safari, elements
   * were shrinking unexpectedly. Setting flexShrink manually on the child elements
   * was a recommended solution to avoid this. We also apply this to
   * the children of 'main' below. See PR #4704 for more info.
   */
  '& > *': {
    flexShrink: 0
  },
  main: {
    '& > *': {
      flexShrink: 0
    },
    display: 'flex',
    flex: '1 0 auto',
    flexDirection: 'column',
    boxSizing: 'border-box',
    padding: 0,
    ...theme.mixins.centeredContainer(theme.currentMaxWidth),
    [theme.mediaQueries.lg]: {
      flexDirection: 'row'
    }
  },
  [theme.mediaQueries.widget]: {
    borderRight: theme.border,
    borderLeft: theme.border
  }
}))

const Sidebar = styled.aside(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  flexDirection: 'column',
  flexShrink: 0,
  flex: 1,
  padding: theme.mixins.pxSpread([
    theme.space[4],
    theme.space[SIDE_PADDING],
    theme.space[4],
    theme.space[SIDE_PADDING]
  ]),
  boxSizing: 'border-box',

  [theme.mediaQueries.lg]: {
    backgroundColor: theme.colors.background.secondary
  }
}))
